import {
  ApplyReasonCode,
  ChartAxisEditInfo,
  ChartAxisInfo,
  ChartAreaInfo,
  ChartEditInfo,
  ChartGroupInfo,
  ChartInfo,
  PlotAreaInfo,
  ChartSeriesEditInfo,
  ChartSeriesInfo,
  PresentationModification,
  ShapeModification,
  SlideModification,
} from "../types";
import { SUPPORTED_XL_CHART_TYPES, type XlChartType } from "../../shared/charts/nativeChartTypes";

export { SUPPORTED_XL_CHART_TYPES } from "../../shared/charts/nativeChartTypes";

/**
 * Normalization for in-slide native charts. Walks `slides[].shapes[]` and
 * validates / coerces each `shape.chart` payload to match the FactSet COM wire
 * format used by `PerformBatchPptOperations` (see f2_docs/charting/operations.*.json).
 *
 * Key concerns:
 * - `categories`, `values`, `xValues`, `bubbleSizes` must be stringified JSON
 *   arrays. If a model emits raw arrays we coerce them.
 * - `colorRgb` values must be 6-digit uppercase hex without the leading '#'.
 * - `chartType` must be one of the supported XlChartType tokens.
 * - Combo charts (per-series `chartType` overrides) with mixed scatter+bar
 *   types are flagged as risky and remapped to safer line+bar.
 */

const SUPPORTED_XL_CHART_TYPE_SET = new Set<string>(SUPPORTED_XL_CHART_TYPES);
const CHART_PATCH_KEYS = new Set([
  "type",
  "id",
  "index",
  "name",
  "chartType",
  "title",
  "legend",
  "series",
  "axes",
  "position",
  "chartGroups",
  "doughnutHoleSize",
  "firstSliceAngle",
  "gapWidth",
  "overlap",
]);
const CHART_EDIT_KEYS = new Set(["title", "legend", "series", "axes", "chartArea", "plotArea"]);
const CHART_TITLE_KEYS = new Set(["hasTitle", "text", "includeInLayout", "font"]);
const CHART_LEGEND_KEYS = new Set(["hasLegend", "position", "includeInLayout", "font"]);
const CHART_AREA_KEYS = new Set(["fill", "line", "font"]);
const PLOT_AREA_KEYS = new Set(["fill", "line"]);
const CHART_GROUP_KEYS = new Set([
  "index",
  "gapWidth",
  "overlap",
  "varyByCategories",
  "firstSliceAngle",
  "doughnutHoleSize",
  "splitType",
  "splitValue",
  "secondPlotSize",
  "bubbleScale",
  "showNegativeBubbles",
  "sizeRepresents",
  "hasSeriesLines",
  "hasDropLines",
  "hasHiLoLines",
  "hasUpDownBars",
  "axisGroup",
]);
const CHART_SERIES_EDIT_KEYS = new Set([
  "index",
  "name",
  "categories",
  "values",
  "xValues",
  "bubbleSizes",
  "chartType",
  "axisGroup",
  "plotOrder",
  "smooth",
  "explosion",
  "invertIfNegative",
  "hasDataLabels",
  "fill",
  "line",
  "marker",
  "dataLabels",
  "points",
]);
const CHART_AXIS_EDIT_KEYS = new Set([
  "axisType",
  "axisGroup",
  "enabled",
  "hasTitle",
  "titleText",
  "titleFont",
  "minimumScale",
  "maximumScale",
  "majorUnit",
  "minorUnit",
  "hasMajorGridlines",
  "hasMinorGridlines",
  "reversePlotOrder",
  "numberFormat",
  "tickLabelFont",
  "majorTickMark",
  "minorTickMark",
  "axisLine",
  "majorGridLine",
  "minorGridLine",
]);
const CHART_POINT_EDIT_KEYS = new Set(["index", "fill", "line", "explosion", "hasDataLabel", "dataLabel"]);

const XL_TICK_MARK_SET = new Set([
  "xlTickMarkNone",
  "xlTickMarkInside",
  "xlTickMarkOutside",
  "xlTickMarkCross",
]);

export interface NativeChartNormalizationIssue {
  code: ApplyReasonCode;
  message: string;
}

export interface NativeChartNormalizationStats {
  touchedCharts: number;
  droppedOptions: number;
  normalizedOptions: number;
  remappedTypes: number;
  rejectedCharts: number;
  issues: NativeChartNormalizationIssue[];
}

function emptyStats(): NativeChartNormalizationStats {
  return {
    touchedCharts: 0,
    droppedOptions: 0,
    normalizedOptions: 0,
    remappedTypes: 0,
    rejectedCharts: 0,
    issues: [],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function dropUnsupportedKeys(
  record: Record<string, unknown>,
  allowedKeys: Set<string>,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  for (const key of Object.keys(record)) {
    if (!allowedKeys.has(key)) {
      delete record[key];
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped unsupported ${label} option '${key}'.`,
      });
    }
  }
}

function normalizeColorRgbOnRecord(
  record: Record<string, unknown>,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  if (record.colorRgb === undefined) {
    return;
  }
  const normalized = normalizeChartColorRgb(record.colorRgb);
  if (normalized) {
    if (normalized !== record.colorRgb) {
      record.colorRgb = normalized;
      stats.normalizedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
        message: `Normalized ${label}.colorRgb to uppercase hex without '#'.`,
      });
    }
  } else {
    delete record.colorRgb;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: `Dropped invalid ${label}.colorRgb.`,
    });
  }
}

function normalizeRecordObjectField(
  parent: Record<string, unknown>,
  key: string,
  stats: NativeChartNormalizationStats,
  label: string
): Record<string, unknown> | null {
  const value = parent[key];
  if (value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    delete parent[key];
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: `Dropped malformed ${label}.`,
    });
    return null;
  }
  return value;
}

function normalizeColorObjectField(
  parent: Record<string, unknown>,
  key: string,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  const record = normalizeRecordObjectField(parent, key, stats, label);
  if (record) {
    normalizeColorRgbOnRecord(record, stats, label);
  }
}

function normalizeDataLabelsField(
  parent: Record<string, unknown>,
  key: string,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  const record = normalizeRecordObjectField(parent, key, stats, label);
  if (!record) {
    return;
  }
  normalizeColorObjectField(record, "font", stats, `${label}.font`);
  normalizeColorObjectField(record, "fill", stats, `${label}.fill`);
  normalizeColorObjectField(record, "border", stats, `${label}.border`);
}

function normalizeMarkerField(
  parent: Record<string, unknown>,
  key: string,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  const record = normalizeRecordObjectField(parent, key, stats, label);
  if (!record) {
    return;
  }
  normalizeColorObjectField(record, "fill", stats, `${label}.fill`);
  normalizeColorObjectField(record, "line", stats, `${label}.line`);
}

function normalizeFiniteNumberField(
  record: Record<string, unknown>,
  key: string,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  const value = record[key];
  if (value === undefined) {
    return;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return;
  }
  delete record[key];
  stats.droppedOptions += 1;
  stats.issues.push({
    code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
    message: `Dropped invalid numeric ${label}.${key}.`,
  });
}

function normalizeChartAreaRecord(
  area: Record<string, unknown>,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  normalizeColorObjectField(area, "fill", stats, `${label}.fill`);
  normalizeColorObjectField(area, "line", stats, `${label}.line`);
  normalizeColorObjectField(area, "font", stats, `${label}.font`);
}

/**
 * Normalize a hex color token into `RRGGBB` uppercase, no leading `#`.
 * This matches the f2_docs golden wire format (e.g. "2B5680").
 */
export function normalizeChartColorRgb(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const raw = value.trim().replace(/^#/, "");
  if (raw.length === 3 && /^[0-9a-fA-F]{3}$/.test(raw)) {
    return raw
      .split("")
      .map((char) => char + char)
      .join("")
      .toUpperCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return raw.toUpperCase();
  }
  return null;
}

/**
 * Coerce a numeric/string array into a stringified JSON array.
 * Accepts already-stringified arrays (returned as-is after JSON round trip
 * to canonicalize whitespace).
 */
function coerceStringifiedArray(
  value: unknown
): { stringified: string | null; touched: boolean } {
  if (value === undefined || value === null) {
    return { stringified: null, touched: false };
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return { stringified: null, touched: false };
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        return { stringified: null, touched: true };
      }
      const canonical = JSON.stringify(parsed);
      return { stringified: canonical, touched: canonical !== trimmed };
    } catch {
      return { stringified: null, touched: true };
    }
  }
  if (Array.isArray(value)) {
    return { stringified: JSON.stringify(value), touched: true };
  }
  return { stringified: null, touched: true };
}

/**
 * Detect whether a `values` stringified-JSON array looks like fractional
 * percentages. Returns true iff every numeric value is in (-1, 1) and at
 * least one is non-zero. Used to decide whether to multiply by 100 so
 * PowerPoint's COM Chart op doesn't pin the secondary axis to 0.4-0.5.
 */
function looksLikeFractionalPercentValues(stringifiedValues: string): boolean {
  try {
    const parsed = JSON.parse(stringifiedValues);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return false;
    }
    let hasNonZero = false;
    for (const raw of parsed) {
      if (raw === null || raw === undefined) continue;
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(num)) {
        return false;
      }
      if (Math.abs(num) >= 1) {
        return false;
      }
      if (num !== 0) {
        hasNonZero = true;
      }
    }
    return hasNonZero;
  } catch {
    return false;
  }
}

function multiplyStringifiedValuesByHundred(stringifiedValues: string): string | null {
  try {
    const parsed = JSON.parse(stringifiedValues);
    if (!Array.isArray(parsed)) {
      return null;
    }
    const scaled = parsed.map((raw) => {
      if (raw === null || raw === undefined) return raw;
      const num = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(num)) return raw;
      // Round to 2 decimal places to avoid floating-point drift like
      // 0.47 * 100 -> 47.00000000000001.
      return Math.round(num * 100 * 100) / 100;
    });
    return JSON.stringify(scaled);
  } catch {
    return null;
  }
}

/**
 * A series triggers fractional-percent coercion only when it is explicitly
 * tagged for the secondary value axis. We deliberately avoid name-based
 * heuristics ("growth", "margin", "rate", ...) because they generate false
 * positives on legitimate sub-1 ratios (e.g. "Conversion factor: 0.2/0.3").
 * Pairing the strict axisGroup check with the all-in-(-1, 1) values check
 * makes coercion deterministic.
 */
function isPercentLikeSeries(series: ChartSeriesInfo): boolean {
  return series.axisGroup === "xlSecondary";
}

function normalizeSeries(
  series: ChartSeriesInfo,
  seriesIndex: number,
  stats: NativeChartNormalizationStats
): { ok: boolean } {
  if (!series.name || typeof series.name !== "string") {
    series.name = `Series ${seriesIndex + 1}`;
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
      message: `Filled missing chart series name at index ${seriesIndex}.`,
    });
  }
  // FactSet's Chart op treats series[].index as 0-based and uses it to slot
  // data into the underlying Excel series. Models routinely emit 1-based
  // indices (e.g. `index: 1` for the only/first series), which causes the
  // chart frame to render but the bars to never appear and the COM call to
  // fail with HRESULT 0x800A03EC. Force series.index to match its array
  // position so the wire payload always matches its physical slot.
  if (series.index !== seriesIndex) {
    if (series.index !== undefined) {
      stats.normalizedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
        message: `Corrected chart series index from ${series.index} to ${seriesIndex} (must be 0-based and match array position).`,
      });
    }
    series.index = seriesIndex;
  }

  for (const field of ["categories", "values", "xValues", "bubbleSizes"] as const) {
    const result = coerceStringifiedArray(series[field]);
    if (result.touched) {
      if (result.stringified === null) {
        delete series[field];
        stats.droppedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
          message: `Dropped invalid '${field}' on chart series ${seriesIndex} (expected stringified JSON array).`,
        });
      } else {
        series[field] = result.stringified;
        stats.normalizedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
          message: `Normalized chart series '${field}' at index ${seriesIndex} to stringified JSON array.`,
        });
      }
    }
  }

  // `values` is required; if it is missing or non-string after normalization,
  // signal the chart should be rejected.
  if (typeof series.values !== "string" || series.values.length === 0) {
    return { ok: false };
  }

  if (series.chartType && !SUPPORTED_XL_CHART_TYPE_SET.has(series.chartType)) {
    stats.remappedTypes += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_TYPE_REMAPPED",
      message: `Removed unsupported per-series chartType '${series.chartType}' on series index ${seriesIndex}.`,
    });
    delete series.chartType;
  }

  if (series.fill?.colorRgb !== undefined) {
    const normalized = normalizeChartColorRgb(series.fill.colorRgb);
    if (normalized) {
      if (normalized !== series.fill.colorRgb) {
        series.fill.colorRgb = normalized;
        stats.normalizedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
          message: `Normalized chart series ${seriesIndex} fill.colorRgb to uppercase hex without '#'.`,
        });
      }
    } else {
      delete series.fill.colorRgb;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped invalid fill.colorRgb on chart series ${seriesIndex}.`,
      });
    }
  }

  if (series.line?.colorRgb !== undefined) {
    const normalized = normalizeChartColorRgb(series.line.colorRgb);
    if (normalized) {
      if (normalized !== series.line.colorRgb) {
        series.line.colorRgb = normalized;
        stats.normalizedOptions += 1;
      }
    } else {
      delete series.line.colorRgb;
      stats.droppedOptions += 1;
    }
  }

  if (series.marker?.fill?.colorRgb !== undefined) {
    const normalized = normalizeChartColorRgb(series.marker.fill.colorRgb);
    if (normalized) {
      series.marker.fill.colorRgb = normalized;
    } else {
      delete series.marker.fill.colorRgb;
      stats.droppedOptions += 1;
    }
  }

  return { ok: true };
}

function normalizeAxes(
  axes: ChartAxisInfo[],
  stats: NativeChartNormalizationStats
): ChartAxisInfo[] {
  return axes.filter((axis) => {
    if (!axis || typeof axis !== "object") {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: "Dropped malformed chart axis entry.",
      });
      return false;
    }
    if (
      axis.axisType !== "xlCategory" &&
      axis.axisType !== "xlValue" &&
      axis.axisType !== "xlSeriesAxis"
    ) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped chart axis with unsupported axisType '${String((axis as { axisType?: unknown }).axisType)}'.`,
      });
      return false;
    }
    if (
      axis.axisGroup !== undefined &&
      axis.axisGroup !== "xlPrimary" &&
      axis.axisGroup !== "xlSecondary"
    ) {
      axis.axisGroup = "xlPrimary";
      stats.normalizedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
        message: "Coerced chart axisGroup to 'xlPrimary'.",
      });
    }
    normalizeTickMarkFields(axis as unknown as Record<string, unknown>, stats, "chart.axes");
    return true;
  });
}

function normalizeTickMarkFields(
  axis: Record<string, unknown>,
  stats: NativeChartNormalizationStats,
  label: string
): void {
  for (const field of ["majorTickMark", "minorTickMark"] as const) {
    if (axis[field] === undefined) {
      continue;
    }
    if (typeof axis[field] !== "string" || !XL_TICK_MARK_SET.has(axis[field] as string)) {
      delete axis[field];
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped unsupported ${label}.${field}.`,
      });
    }
  }
}

function normalizeChartGroups(
  chartGroups: unknown[],
  stats: NativeChartNormalizationStats,
  label: string
): ChartGroupInfo[] {
  const normalizedGroups: ChartGroupInfo[] = [];
  for (let idx = 0; idx < chartGroups.length; idx += 1) {
    const group = chartGroups[idx];
    if (!isRecord(group)) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped malformed ${label} entry at index ${idx}.`,
      });
      continue;
    }

    dropUnsupportedKeys(group, CHART_GROUP_KEYS, stats, label);
    if (!Number.isInteger(group.index)) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped ${label} entry at index ${idx} because it is missing integer 'index'.`,
      });
      continue;
    }

    for (const field of [
      "gapWidth",
      "overlap",
      "firstSliceAngle",
      "doughnutHoleSize",
      "splitValue",
      "secondPlotSize",
      "bubbleScale",
    ] as const) {
      normalizeFiniteNumberField(group, field, stats, label);
    }

    if (
      group.axisGroup !== undefined &&
      group.axisGroup !== "xlPrimary" &&
      group.axisGroup !== "xlSecondary"
    ) {
      const axisGroup = group.axisGroup;
      delete group.axisGroup;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped unsupported ${label}.axisGroup '${String(axisGroup)}'.`,
      });
    }

    normalizedGroups.push(group as unknown as ChartGroupInfo);
  }
  return normalizedGroups;
}

/**
 * TODO(FactSet ticket: secondary-axis title bug): remove this reorder once
 * FactSet's `PerformBatchPptOperations` Chart op dispatches `axes[]` entries by
 * (axisType, axisGroup) instead of writing every `xlValue` entry to a single
 * primary slot.
 *
 * Current FactSet build behavior (verified by probes A/B/C, 2026-05-11):
 *   - `series[].axisGroup` is honored for data binding (correct).
 *   - `chart.axes[]` xlValue entries are iterated and each one writes to the
 *     same single primary axis slot. Last-write-wins. The xlSecondary entry is
 *     never applied to the actual secondary axis.
 *
 * Schema-correct payload (matches f2_docs/charting/operations.combo-bar-line.json):
 *   axes: [
 *     { axisType: "xlValue", axisGroup: "xlPrimary",   titleText: "Revenue ($M)" },
 *     { axisType: "xlValue", axisGroup: "xlSecondary", titleText: "Margin (%)" },
 *     { axisType: "xlCategory", axisGroup: "xlPrimary", titleText: "Quarter" },
 *   ]
 *
 * In the buggy build the secondary entry comes last among xlValue entries and
 * clobbers the primary axis title with "Margin (%)". By reordering xlValue
 * entries so xlPrimary is LAST, the primary axis ends up correctly titled
 * (Probe B's outcome). The secondary axis stays untitled, which is the best
 * we can do client-side until FactSet ships the fix.
 *
 * The Office.js path is unaffected: nativeChartService looks axes up via
 * findValueAxis(axes, group) by (axisType, axisGroup), so order is irrelevant.
 */
function reorderAxesForPrimaryLastWriteWins(
  axes: ChartAxisInfo[],
  stats: NativeChartNormalizationStats
): ChartAxisInfo[] {
  const isPrimaryValueAxis = (axis: ChartAxisInfo): boolean =>
    axis.axisType === "xlValue" && (axis.axisGroup ?? "xlPrimary") === "xlPrimary";
  const hasPrimaryValue = axes.some(isPrimaryValueAxis);
  const hasSecondaryValue = axes.some(
    (axis) => axis.axisType === "xlValue" && axis.axisGroup === "xlSecondary"
  );
  if (!hasPrimaryValue || !hasSecondaryValue) {
    return axes;
  }
  const primaryValueEntries: ChartAxisInfo[] = [];
  const otherEntries: ChartAxisInfo[] = [];
  for (const axis of axes) {
    if (isPrimaryValueAxis(axis)) {
      primaryValueEntries.push(axis);
    } else {
      otherEntries.push(axis);
    }
  }
  const reordered = [...otherEntries, ...primaryValueEntries];
  if (reordered.some((entry, i) => entry !== axes[i])) {
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
      message:
        "Reordered chart.axes so xlValue+xlPrimary entries come after xlValue+xlSecondary (FactSet COM workaround for combo-chart axis-title last-write-wins bug).",
    });
  }
  return reordered;
}

function hasScatterBarComboRisk(chart: ChartInfo): boolean {
  const rootType = chart.chartType;
  const seriesTypes = new Set<XlChartType>();
  if (rootType) {
    seriesTypes.add(rootType);
  }
  for (const series of chart.series || []) {
    if (series.chartType) {
      seriesTypes.add(series.chartType);
    }
  }
  const hasScatter = Array.from(seriesTypes).some(
    (type) =>
      type === "xlXYScatter" ||
      type === "xlXYScatterLines" ||
      type === "xlXYScatterLinesNoMarkers" ||
      type === "xlBubble" ||
      type === "xlBubble3DEffect"
  );
  const hasBar = Array.from(seriesTypes).some(
    (type) =>
      type === "xlBarClustered" ||
      type === "xlBarStacked" ||
      type === "xlBarStacked100" ||
      type === "xlColumnClustered" ||
      type === "xlColumnStacked" ||
      type === "xlColumnStacked100"
  );
  return hasScatter && hasBar;
}

function remapScatterToLineSafe(
  chart: ChartInfo,
  stats: NativeChartNormalizationStats
): void {
  let remapped = 0;
  for (const series of chart.series || []) {
    if (
      series.chartType === "xlXYScatter" ||
      series.chartType === "xlXYScatterLines" ||
      series.chartType === "xlXYScatterLinesNoMarkers"
    ) {
      series.chartType = "xlLineMarkers";
      if (!series.marker) {
        series.marker = { style: "xlMarkerStyleCircle", size: 6 };
      } else if (!series.marker.style) {
        series.marker.style = "xlMarkerStyleCircle";
      }
      remapped += 1;
    }
  }
  if (remapped > 0) {
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_RISK_REMAPPED",
      message: `Remapped scatter-plus-bar combo to safer line+bar (${remapped} scatter series remapped).`,
    });
  }
}

function clampPosition(
  position: ChartInfo["position"] | undefined,
  stats: NativeChartNormalizationStats
): ChartInfo["position"] | undefined {
  if (!position) {
    return undefined;
  }
  let { left, top, width, height } = position;
  if (
    typeof left !== "number" ||
    typeof top !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_LAYOUT_CLAMPED",
      message: "Dropped invalid chart position; using default sizing.",
    });
    return undefined;
  }

  // Clamp to reasonable slide bounds (assume max 960pt wide, 540pt tall).
  // Shrink charts that extend beyond the right or bottom edge.
  const maxW = 960;
  const maxH = 540;
  if (left < 0) left = 0;
  if (top < 0) top = 0;
  if (left + width > maxW) {
    width = maxW - left;
    if (width < 20) { left = 0; width = maxW * 0.5; }
  }
  if (top + height > maxH) {
    height = maxH - top;
    if (height < 20) { top = 0; height = maxH * 0.4; }
  }
  if (width !== position.width || height !== position.height || left !== position.left || top !== position.top) {
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_LAYOUT_CLAMPED",
      message: `Clamped chart position to stay within slide bounds (${Math.round(left)},${Math.round(top)} ${Math.round(width)}x${Math.round(height)}).`,
    });
  }
  return { left, top, width, height };
}

/**
 * Normalize a single in-slide chart payload in place. Returns false when the
 * chart is missing data so badly that it should be removed from its host shape.
 */
function normalizeChartInfoInPlace(
  chart: ChartInfo,
  stats: NativeChartNormalizationStats
): boolean {
  stats.touchedCharts += 1;
  chart.type = "Chart";

  if (!chart.chartType || !SUPPORTED_XL_CHART_TYPE_SET.has(chart.chartType)) {
    stats.remappedTypes += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_TYPE_REMAPPED",
      message: `Remapped unsupported chartType '${String(
        chart.chartType
      )}' to 'xlColumnClustered'.`,
    });
    chart.chartType = "xlColumnClustered";
  }

  if (!Array.isArray(chart.series) || chart.series.length === 0) {
    stats.rejectedCharts += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_INVALID_NATIVE_CHART",
      message: "Rejected chart with no series data.",
    });
    return false;
  }

  const validSeries: ChartSeriesInfo[] = [];
  for (let idx = 0; idx < chart.series.length; idx += 1) {
    const series = chart.series[idx];
    if (!isRecord(series)) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped malformed chart series at index ${idx}.`,
      });
      continue;
    }
    const result = normalizeSeries(series as ChartSeriesInfo, idx, stats);
    if (result.ok) {
      validSeries.push(series as ChartSeriesInfo);
    } else {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped chart series at index ${idx} due to missing 'values'.`,
      });
    }
  }
  if (validSeries.length === 0) {
    stats.rejectedCharts += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_INVALID_NATIVE_CHART",
      message: "Rejected chart because no series had valid 'values'.",
    });
    return false;
  }
  // Renumber surviving series indices to be contiguous after the filter.
  // `normalizeSeries` aligns each series.index with the ORIGINAL loop
  // position, but if any series was dropped (malformed, missing values),
  // the survivors carry their pre-filter indices (e.g. [1, 2] when the
  // first input series was dropped). That is exactly the 1-based index
  // mismatch we are trying to prevent — FactSet's Chart op uses
  // series.index to slot the data into the underlying Excel series, and
  // an out-of-position index makes the chart frame render with no bars
  // and the COM call fail with HRESULT 0x800A03EC.
  let renumbered = 0;
  for (let finalIdx = 0; finalIdx < validSeries.length; finalIdx += 1) {
    if (validSeries[finalIdx].index !== finalIdx) {
      validSeries[finalIdx].index = finalIdx;
      renumbered += 1;
    }
  }
  if (renumbered > 0) {
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
      message: `Renumbered ${renumbered} chart series indices to contiguous 0-based positions after filtering dropped series.`,
    });
  }
  chart.series = validSeries;

  if (Array.isArray(chart.axes)) {
    chart.axes = normalizeAxes(chart.axes, stats);
    chart.axes = reorderAxesForPrimaryLastWriteWins(chart.axes, stats);
  }

  if (hasScatterBarComboRisk(chart)) {
    remapScatterToLineSafe(chart, stats);
  }

  chart.position = clampPosition(chart.position, stats);

  if (chart.title?.text && typeof chart.title.text !== "string") {
    delete chart.title.text;
  }

  // TODO(FactSet: https://github.com/factset-finster/f2_docs/issues/137):
  // create ignores title.hasTitle=false and falls back to the series name as
  // the native title. hasTitle:true + blank text suppresses that chrome.
  // Do NOT apply this on modify/chartEdit — hasTitle:false works there.
  if (chart.title?.hasTitle === false) {
    chart.title = { hasTitle: true, text: "" };
    stats.normalizedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
      message:
        'Rewrote create chart title { hasTitle: false } to { hasTitle: true, text: "" } (FactSet create ignores hasTitle:false).',
    });
  }

  // Legends are now driven entirely by the Chart legend rule in the prompt
  // (slideGenerationPrompts.ts / systemPrompts.ts): pie/doughnut and
  // single-series charts emit `{ hasLegend: false }`, multi-series charts
  // emit `{ hasLegend: true, position, font }`, and explicit user requests
  // produce a full formatted block. We no longer strip the legend here —
  // doing so would erase the AI's correctly-emitted disable toggles and
  // formatted legend blocks alike, and force PowerPoint into chart-type
  // defaults that don't match user intent (line/scatter charts default to
  // legend ON, which is the opposite of what single-series should show).
  //
  // The historical FactSet COM bug ("This object doesn't have a legend.")
  // only fires when `hasLegend: false` is combined with other legend
  // properties (position / font / includeInLayout / legendEntries). The
  // prompt explicitly forbids that combination; if it ever slips through,
  // FactSet's op will report Failed with the COM error in failureReason,
  // surfacing it as a model-output regression rather than hiding it here.

  // Convert fractional-percent values to whole numbers and tag the matching
  // value axis with a "0\"%\"" numberFormat. Fractional percents (e.g.
  // [0.44, 0.45, 0.47, 0.49]) cause PowerPoint's COM Chart op to pin the
  // secondary axis to the 0.4-0.5 range and clip column bars from the
  // primary series to that tiny scale. Whole-number percents (44, 45, etc.)
  // with explicit numberFormat match the f2_docs combo-bar-line golden.
  for (const series of chart.series ?? []) {
    if (
      typeof series.values === "string" &&
      isPercentLikeSeries(series) &&
      looksLikeFractionalPercentValues(series.values)
    ) {
      const scaled = multiplyStringifiedValuesByHundred(series.values);
      if (scaled) {
        series.values = scaled;
        stats.normalizedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_PERCENT_COERCED",
          message: `Coerced fractional percent values on series '${series.name}' to whole numbers; PowerPoint axis scale would otherwise be pinned to 0-1.`,
        });
        // Adjust the matching value axis on the same axisGroup so its
        // tick format and explicit bounds track the now-scaled values.
        // The numberFormat is only injected when the model didn't already
        // provide one (so we never overwrite an explicit choice), but the
        // bounds MUST be scaled unconditionally whenever we coerce values
        // — otherwise the axis renders 0.4-0.5 while data is 44-49 and
        // PowerPoint clips/hides the series. Doesn't matter if the chart
        // has no axes array — PowerPoint will autoformat.
        const targetGroup = series.axisGroup ?? "xlSecondary";
        if (Array.isArray(chart.axes)) {
          const matchingAxis = chart.axes.find(
            (axis) => axis.axisType === "xlValue" && axis.axisGroup === targetGroup
          );
          if (matchingAxis) {
            if (!matchingAxis.numberFormat) {
              matchingAxis.numberFormat = '0"%"';
            }
            // Both bound checks use the same inclusive predicate (|x| <= 1)
            // because this branch only runs when every series value is in
            // (-1, 1); a bound at exactly ±1.0 is a fraction expressing
            // ±100%, not a whole-number percent. Treating min and max
            // asymmetrically (strict vs inclusive) would leave bounds like
            // `{ minimumScale: 1.0, maximumScale: 2.0 }` half-scaled
            // (1.0 stays as 1, 2.0 stays as 2) while values become 120-200,
            // clipping the entire series.
            if (
              typeof matchingAxis.minimumScale === "number" &&
              Math.abs(matchingAxis.minimumScale) <= 1
            ) {
              matchingAxis.minimumScale = Math.round(matchingAxis.minimumScale * 100);
            }
            if (
              typeof matchingAxis.maximumScale === "number" &&
              Math.abs(matchingAxis.maximumScale) <= 1
            ) {
              matchingAxis.maximumScale = Math.round(matchingAxis.maximumScale * 100);
            }
          }
        }
      }
    }
  }

  return true;
}

function normalizeChartEditSeries(
  series: unknown[],
  stats: NativeChartNormalizationStats
): ChartSeriesEditInfo[] {
  const normalizedSeries: ChartSeriesEditInfo[] = [];
  for (let idx = 0; idx < series.length; idx += 1) {
    const rawSeries = series[idx];
    if (!isRecord(rawSeries)) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped malformed chartEdit series at index ${idx}.`,
      });
      continue;
    }
    dropUnsupportedKeys(rawSeries, CHART_SERIES_EDIT_KEYS, stats, "chartEdit.series");
    if (!Number.isInteger(rawSeries.index) || Number(rawSeries.index) < 0) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped chartEdit series at index ${idx} because it lacks a 0-based numeric 'index'.`,
      });
      continue;
    }

    for (const field of ["categories", "values", "xValues", "bubbleSizes"] as const) {
      const result = coerceStringifiedArray(rawSeries[field]);
      if (result.touched) {
        if (result.stringified === null) {
          delete rawSeries[field];
          stats.droppedOptions += 1;
          stats.issues.push({
            code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
            message: `Dropped invalid '${field}' on chartEdit series ${rawSeries.index} (expected stringified JSON array).`,
          });
        } else {
          rawSeries[field] = result.stringified;
          stats.normalizedOptions += 1;
          stats.issues.push({
            code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
            message: `Normalized chartEdit series '${field}' at index ${rawSeries.index} to stringified JSON array.`,
          });
        }
      }
    }

    if (
      rawSeries.chartType !== undefined &&
      (typeof rawSeries.chartType !== "string" || !SUPPORTED_XL_CHART_TYPE_SET.has(rawSeries.chartType))
    ) {
      delete rawSeries.chartType;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped unsupported chartType on chartEdit series ${rawSeries.index}.`,
      });
    }

    if (
      rawSeries.axisGroup !== undefined &&
      rawSeries.axisGroup !== "xlPrimary" &&
      rawSeries.axisGroup !== "xlSecondary"
    ) {
      delete rawSeries.axisGroup;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped invalid axisGroup on chartEdit series ${rawSeries.index}.`,
      });
    }

    normalizeColorObjectField(rawSeries, "fill", stats, `chartEdit.series[${rawSeries.index}].fill`);
    normalizeColorObjectField(rawSeries, "line", stats, `chartEdit.series[${rawSeries.index}].line`);
    normalizeMarkerField(rawSeries, "marker", stats, `chartEdit.series[${rawSeries.index}].marker`);
    normalizeDataLabelsField(
      rawSeries,
      "dataLabels",
      stats,
      `chartEdit.series[${rawSeries.index}].dataLabels`
    );

    if (Array.isArray(rawSeries.points)) {
      const normalizedPoints: unknown[] = [];
      for (let pointIdx = 0; pointIdx < rawSeries.points.length; pointIdx += 1) {
        const point = rawSeries.points[pointIdx];
        if (!isRecord(point)) {
          stats.droppedOptions += 1;
          stats.issues.push({
            code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
            message: `Dropped malformed chartEdit point at series ${rawSeries.index}, point ${pointIdx}.`,
          });
          continue;
        }
        dropUnsupportedKeys(point, CHART_POINT_EDIT_KEYS, stats, "chartEdit.series.points");
        if (!Number.isInteger(point.index) || Number(point.index) < 0) {
          stats.droppedOptions += 1;
          stats.issues.push({
            code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
            message: `Dropped chartEdit point at series ${rawSeries.index}, point ${pointIdx} because it lacks a 0-based numeric 'index'.`,
          });
          continue;
        }
        normalizeColorObjectField(
          point,
          "fill",
          stats,
          `chartEdit.series[${rawSeries.index}].points[${point.index}].fill`
        );
        normalizeColorObjectField(
          point,
          "line",
          stats,
          `chartEdit.series[${rawSeries.index}].points[${point.index}].line`
        );
        normalizeDataLabelsField(
          point,
          "dataLabel",
          stats,
          `chartEdit.series[${rawSeries.index}].points[${point.index}].dataLabel`
        );
        normalizedPoints.push(point);
      }
      rawSeries.points = normalizedPoints;
    } else if (rawSeries.points !== undefined) {
      delete rawSeries.points;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped malformed points array on chartEdit series ${rawSeries.index}.`,
      });
    }

    normalizedSeries.push(rawSeries as unknown as ChartSeriesEditInfo);
  }
  return normalizedSeries;
}

function normalizeSparseChartPatchInPlace(
  chart: ChartInfo,
  stats: NativeChartNormalizationStats
): boolean {
  stats.touchedCharts += 1;
  chart.type = "Chart";
  dropUnsupportedKeys(chart as unknown as Record<string, unknown>, CHART_PATCH_KEYS, stats, "chart");

  if (chart.chartType !== undefined && !SUPPORTED_XL_CHART_TYPE_SET.has(chart.chartType)) {
    stats.remappedTypes += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_TYPE_REMAPPED",
      message: `Removed unsupported chartType '${String(chart.chartType)}' from sparse chart patch.`,
    });
    delete chart.chartType;
  }

  if (Array.isArray(chart.axes)) {
    chart.axes = normalizeAxes(chart.axes, stats);
    chart.axes = reorderAxesForPrimaryLastWriteWins(chart.axes, stats);
  } else if (chart.axes !== undefined) {
    delete chart.axes;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: "Dropped malformed sparse chart axes.",
    });
  }

  if (Array.isArray(chart.chartGroups)) {
    chart.chartGroups = normalizeChartGroups(chart.chartGroups, stats, "chart.chartGroups");
  } else if (chart.chartGroups !== undefined) {
    delete chart.chartGroups;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: "Dropped malformed sparse chart chartGroups.",
    });
  }

  if (Array.isArray(chart.series)) {
    chart.series = normalizeChartEditSeries(
      chart.series,
      stats
    ) as unknown as ChartSeriesInfo[];
  } else if (chart.series !== undefined) {
    delete chart.series;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: "Dropped malformed sparse chart series.",
    });
  }

  if (chart.position !== undefined) {
    const clampedPosition = clampPosition(chart.position, stats);
    if (clampedPosition) {
      chart.position = clampedPosition;
    } else {
      delete chart.position;
    }
  }

  const chartRecord = chart as unknown as Record<string, unknown>;
  const remainingPatchKeys = Object.keys(chartRecord).filter(
    (key) => key !== "type" && chartRecord[key] !== undefined
  );
  if (remainingPatchKeys.length === 0) {
    stats.rejectedCharts += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_INVALID_NATIVE_CHART",
      message: "Rejected sparse chart patch because no valid chart fields remained.",
    });
    return false;
  }

  return true;
}

function normalizeChartEditAxes(
  axes: unknown[],
  stats: NativeChartNormalizationStats
): ChartAxisEditInfo[] {
  const normalizedAxes: ChartAxisEditInfo[] = [];
  for (let idx = 0; idx < axes.length; idx += 1) {
    const rawAxis = axes[idx];
    if (!isRecord(rawAxis)) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped malformed chartEdit axis at index ${idx}.`,
      });
      continue;
    }
    dropUnsupportedKeys(rawAxis, CHART_AXIS_EDIT_KEYS, stats, "chartEdit.axes");
    if (
      rawAxis.axisType !== "xlCategory" &&
      rawAxis.axisType !== "xlValue" &&
      rawAxis.axisType !== "xlSeriesAxis"
    ) {
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: `Dropped chartEdit axis with unsupported axisType '${String(rawAxis.axisType)}'.`,
      });
      continue;
    }
    if (
      rawAxis.axisGroup !== undefined &&
      rawAxis.axisGroup !== "xlPrimary" &&
      rawAxis.axisGroup !== "xlSecondary"
    ) {
      rawAxis.axisGroup = "xlPrimary";
      stats.normalizedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
        message: "Coerced chartEdit axisGroup to 'xlPrimary'.",
      });
    }
    normalizeTickMarkFields(rawAxis, stats, "chartEdit.axes");
    normalizeColorObjectField(rawAxis, "titleFont", stats, "chartEdit.axes.titleFont");
    normalizeColorObjectField(rawAxis, "tickLabelFont", stats, "chartEdit.axes.tickLabelFont");
    normalizeColorObjectField(rawAxis, "axisLine", stats, "chartEdit.axes.axisLine");
    normalizeColorObjectField(rawAxis, "majorGridLine", stats, "chartEdit.axes.majorGridLine");
    normalizeColorObjectField(rawAxis, "minorGridLine", stats, "chartEdit.axes.minorGridLine");
    for (const field of ["minimumScale", "maximumScale", "majorUnit", "minorUnit"] as const) {
      normalizeFiniteNumberField(rawAxis, field, stats, "chartEdit.axes");
    }
    normalizedAxes.push(rawAxis as unknown as ChartAxisEditInfo);
  }
  return reorderAxesForPrimaryLastWriteWins(normalizedAxes as ChartAxisInfo[], stats) as ChartAxisEditInfo[];
}

function normalizeChartEditArea(
  chartEdit: ChartEditInfo,
  key: "chartArea" | "plotArea",
  allowedKeys: Set<string>,
  stats: NativeChartNormalizationStats
): void {
  const record = normalizeRecordObjectField(
    chartEdit as unknown as Record<string, unknown>,
    key,
    stats,
    `chartEdit.${key}`
  );
  if (!record) {
    return;
  }
  dropUnsupportedKeys(record, allowedKeys, stats, `chartEdit.${key}`);
  normalizeChartAreaRecord(record, stats, `chartEdit.${key}`);
  if (key === "chartArea") {
    chartEdit.chartArea = record as unknown as ChartAreaInfo;
  } else {
    chartEdit.plotArea = record as unknown as PlotAreaInfo;
  }
}

function normalizeChartEditInfoInPlace(
  chartEdit: ChartEditInfo,
  stats: NativeChartNormalizationStats
): void {
  stats.touchedCharts += 1;
  dropUnsupportedKeys(chartEdit as Record<string, unknown>, CHART_EDIT_KEYS, stats, "chartEdit");

  if (chartEdit.title !== undefined) {
    if (!isRecord(chartEdit.title)) {
      delete chartEdit.title;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: "Dropped malformed chartEdit.title.",
      });
    } else if (chartEdit.title.hasTitle === false) {
      const titleKeys = Object.keys(chartEdit.title);
      if (titleKeys.length !== 1 || titleKeys[0] !== "hasTitle") {
        chartEdit.title = { hasTitle: false };
        stats.normalizedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
          message: "Normalized disabled chart title to only { hasTitle: false }.",
        });
      }
    } else {
      dropUnsupportedKeys(
        chartEdit.title as Record<string, unknown>,
        CHART_TITLE_KEYS,
        stats,
        "chartEdit.title"
      );
      normalizeColorObjectField(
        chartEdit.title as Record<string, unknown>,
        "font",
        stats,
        "chartEdit.title.font"
      );
    }
  }

  if (chartEdit.legend !== undefined) {
    if (!isRecord(chartEdit.legend)) {
      delete chartEdit.legend;
      stats.droppedOptions += 1;
      stats.issues.push({
        code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
        message: "Dropped malformed chartEdit.legend.",
      });
    } else if (chartEdit.legend.hasLegend === false) {
      const legendKeys = Object.keys(chartEdit.legend);
      if (legendKeys.length !== 1 || legendKeys[0] !== "hasLegend") {
        chartEdit.legend = { hasLegend: false };
        stats.normalizedOptions += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_CHART_OPTION_NORMALIZED",
          message: "Normalized disabled chart legend to only { hasLegend: false }.",
        });
      }
    } else {
      dropUnsupportedKeys(
        chartEdit.legend as Record<string, unknown>,
        CHART_LEGEND_KEYS,
        stats,
        "chartEdit.legend"
      );
      normalizeColorObjectField(
        chartEdit.legend as Record<string, unknown>,
        "font",
        stats,
        "chartEdit.legend.font"
      );
    }
  }

  if (Array.isArray(chartEdit.series)) {
    chartEdit.series = normalizeChartEditSeries(chartEdit.series, stats);
  } else if (chartEdit.series !== undefined) {
    delete chartEdit.series;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: "Dropped malformed chartEdit.series.",
    });
  }

  if (Array.isArray(chartEdit.axes)) {
    chartEdit.axes = normalizeChartEditAxes(chartEdit.axes, stats);
  } else if (chartEdit.axes !== undefined) {
    delete chartEdit.axes;
    stats.droppedOptions += 1;
    stats.issues.push({
      code: "MODEL_OUTPUT_CHART_OPTION_DROPPED",
      message: "Dropped malformed chartEdit.axes.",
    });
  }

  if (chartEdit.chartArea !== undefined) {
    normalizeChartEditArea(chartEdit, "chartArea", CHART_AREA_KEYS, stats);
  }

  if (chartEdit.plotArea !== undefined) {
    normalizeChartEditArea(chartEdit, "plotArea", PLOT_AREA_KEYS, stats);
  }
}

/**
 * Walk `modifications.slides[].shapes[]` and normalize every `shape.chart`
 * and `shape.chartEdit` payload in place. Shapes whose full chart payload
 * cannot be repaired have their `chart` field cleared and the shape may be
 * removed by the caller, but we keep the shape around (without chart) so a
 * diagnostic is the only side effect.
 */
export function normalizeInSlideChartsInModifications(
  modifications: PresentationModification
): NativeChartNormalizationStats {
  const stats = emptyStats();
  const slides: SlideModification[] = Array.isArray(modifications.slides)
    ? modifications.slides
    : [];

  for (const slide of slides) {
    const shapes: ShapeModification[] = Array.isArray(slide.shapes) ? slide.shapes : [];
    for (const shape of shapes) {
      if (!shape) {
        continue;
      }
      if (shape.chart) {
        const ok =
          shape.action === "modify"
            ? normalizeSparseChartPatchInPlace(shape.chart, stats)
            : normalizeChartInfoInPlace(shape.chart, stats);
        if (!ok) {
          delete shape.chart;
        }
      }
      if (shape.chartEdit) {
        normalizeChartEditInfoInPlace(shape.chartEdit, stats);
      }
    }
  }

  return stats;
}
